import { useEffect, useRef } from 'react';

/**
 * Implements setInterval() in a declarative manner.
 *
 * @param {Function} callback - A function to be executed every delay milliseconds.
 *    The first execution happens after delay milliseconds.
 * @param {Number|Null} [delay=0] - The time, in milliseconds, the timer should delay
 *    in between executions of the specified function or code.
 *    If the delay is null, the interval will not be created or cleared if it was.
 *    Defaults to 0 if not specified.
 *
 * @see https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 *
 * @see packages/utils/stories/useInterval.stories.tsx
 *
 * @example
 * export function Counter() {
 *     const [count, setCount] = useState(0);
 *
 *     useInterval(() => {
 *         setCount((prevCount) => prevCount + 1);
 *     }, 1000);
 *
 *     return (
 *         <div className="counter">
 *             <h1>Count: {count}</h1>
 *         </div>
 *     );
 * }
 */
export function useInterval(callback: VoidFunction, delay: number | null = 0) {
    const savedCallback = useRef(callback);

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        if (delay === null) {
            return undefined;
        }

        const id = setInterval(savedCallback.current, delay);

        return () => clearInterval(id);
    }, [savedCallback, delay]);
}
